import { useEffect, useState } from 'react';
import type { SettingsInput, TenantSettings } from '@provalia/shared';
import { Card, ErrorBox, Field, Loading, errorText, useToast } from './ui';

/**
 * Configuración del portal de un cliente: tolerancias, evaluación, avisos,
 * correo y planos.
 *
 * La usan el panel de ECINSA y el administrador del propio cliente: es la
 * misma configuración, y tenerla en un solo formulario evita que un ajuste
 * exista en una pantalla y no en la otra.
 */
export interface SettingsFormProps {
  load: () => Promise<{ settings: TenantSettings; defaults: TenantSettings }>;
  save: (input: SettingsInput) => Promise<{ settings: TenantSettings }>;
  testEmail: (to: string) => Promise<unknown>;
  /** Correo propuesto para la prueba (el de quien está conectado). */
  defaultTestTo?: string;
}

const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const NOTIFICATIONS: Array<{ key: keyof TenantSettings['notifications']; label: string; who: string }> = [
  { key: 'newOrder', label: 'Pedido nuevo pendiente de aceptar', who: 'Proveedor' },
  { key: 'reminders', label: 'Recordatorios de pedidos sin aceptar', who: 'Proveedor' },
  { key: 'orderModified', label: 'Pedido aceptado que se ha modificado', who: 'Proveedor' },
  { key: 'orderRejected', label: 'Pedido rechazado por el proveedor', who: 'Compras' },
  { key: 'dateOutOfTolerance', label: 'Fecha fuera de tolerancia', who: 'Compras' },
  { key: 'priceOutOfTolerance', label: 'Precio fuera de tolerancia', who: 'Compras' },
];

interface Draft {
  priceTolerancePct: string;
  dateToleranceDays: string;
  evaluationWindowMonths: string;
  weights: { quality: string; punctuality: string; availability: string; agility: string };
  reminderDays: string;
  weeklyDigest: { enabled: boolean; weekday: number; hour: string };
  buyerEmails: string;
  notifications: TenantSettings['notifications'];
  smtp: { host: string; port: string; secure: boolean; username: string; from: string; password: string; hasPassword: boolean };
  drawings: { basePath: string; pattern: string };
}

function toDraft(s: TenantSettings): Draft {
  return {
    priceTolerancePct: String(s.priceTolerancePct),
    dateToleranceDays: String(s.dateToleranceDays),
    evaluationWindowMonths: String(s.evaluationWindowMonths),
    weights: {
      quality: String(s.evaluationWeights.quality),
      punctuality: String(s.evaluationWeights.punctuality),
      availability: String(s.evaluationWeights.availability),
      agility: String(s.evaluationWeights.agility),
    },
    reminderDays: s.reminderDays.join(', '),
    weeklyDigest: { enabled: s.weeklyDigest.enabled, weekday: s.weeklyDigest.weekday, hour: String(s.weeklyDigest.hour) },
    buyerEmails: s.buyerEmails.join('\n'),
    notifications: { ...s.notifications },
    smtp: { ...s.smtp, port: String(s.smtp.port), password: '' },
    drawings: { ...s.drawings },
  };
}

const num = (v: string): number => Number(v.replace(',', '.'));

function toInput(d: Draft): SettingsInput {
  return {
    priceTolerancePct: num(d.priceTolerancePct),
    dateToleranceDays: Math.round(num(d.dateToleranceDays)),
    evaluationWindowMonths: Math.round(num(d.evaluationWindowMonths)),
    evaluationWeights: {
      quality: num(d.weights.quality),
      punctuality: num(d.weights.punctuality),
      availability: num(d.weights.availability),
      agility: num(d.weights.agility),
    },
    reminderDays: d.reminderDays
      .split(/[,\s;]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map(Number),
    weeklyDigest: { enabled: d.weeklyDigest.enabled, weekday: d.weeklyDigest.weekday, hour: Math.round(num(d.weeklyDigest.hour)) },
    buyerEmails: d.buyerEmails
      .split(/[\n,;]+/)
      .map((x) => x.trim())
      .filter(Boolean),
    notifications: d.notifications,
    smtp: {
      host: d.smtp.host.trim(),
      port: Math.round(num(d.smtp.port)),
      secure: d.smtp.secure,
      username: d.smtp.username.trim(),
      from: d.smtp.from.trim(),
      password: d.smtp.password,
    },
    drawings: { basePath: d.drawings.basePath.trim(), pattern: d.drawings.pattern.trim() },
  };
}

export function SettingsForm({ load, save, testEmail, defaultTestTo }: SettingsFormProps): JSX.Element {
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [defaults, setDefaults] = useState<TenantSettings | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [testTo, setTestTo] = useState(defaultTestTo ?? '');
  const [testing, setTesting] = useState(false);

  const reload = async (): Promise<void> => {
    setLoadError(null);
    try {
      const r = await load();
      setDraft(toDraft(r.settings));
      setDefaults(r.defaults);
    } catch (err) {
      setLoadError(err);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadError) return <ErrorBox error={loadError} onRetry={() => void reload()} />;
  if (!draft) return <Loading />;

  const set = (patch: Partial<Draft>): void => setDraft({ ...draft, ...patch });
  const weightSum =
    num(draft.weights.quality) + num(draft.weights.punctuality) + num(draft.weights.availability) + num(draft.weights.agility);

  const onSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await save(toInput(draft));
      setDraft(toDraft(r.settings));
      toast('Configuración guardada', 'ok');
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const onTest = async (): Promise<void> => {
    setTesting(true);
    try {
      await testEmail(testTo);
      toast(`Correo de prueba enviado a ${testTo}`, 'ok');
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="stack lg">
      <Card title="Tolerancias">
        <div className="grid cols-2">
          <Field label="Tolerancia de precio (± %)" hint={`Dentro de ella el precio del albarán se graba solo. Por defecto ${defaults?.priceTolerancePct ?? 5} %.`}>
            <input className="input" inputMode="decimal" value={draft.priceTolerancePct} onChange={(e) => set({ priceTolerancePct: e.target.value })} />
          </Field>
          <Field label="Tolerancia de fecha (± días)" hint={`Dentro de ella la fecha propuesta se graba sola. Por defecto ${defaults?.dateToleranceDays ?? 5} días.`}>
            <input className="input" inputMode="numeric" value={draft.dateToleranceDays} onChange={(e) => set({ dateToleranceDays: e.target.value })} />
          </Field>
        </div>
      </Card>

      <Card title="Evaluación de proveedores">
        <div className="stack">
          <Field label="Ventana de cálculo (meses)" hint="La puntuación se calcula sobre la actividad de estos últimos meses.">
            <input className="input" style={{ maxWidth: 160 }} inputMode="numeric" value={draft.evaluationWindowMonths} onChange={(e) => set({ evaluationWindowMonths: e.target.value })} />
          </Field>
          <div className="grid cols-4">
            {(
              [
                ['quality', 'Calidad de piezas'],
                ['punctuality', 'Puntualidad'],
                ['availability', 'Disponibilidad'],
                ['agility', 'Agilidad de gestión'],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={`${label} (%)`}>
                <input
                  className="input"
                  inputMode="decimal"
                  value={draft.weights[key]}
                  onChange={(e) => set({ weights: { ...draft.weights, [key]: e.target.value } })}
                />
              </Field>
            ))}
          </div>
          <div className={Math.abs(weightSum - 100) < 0.001 ? 'subtle' : 'callout warn'}>
            Los pesos suman {Number.isFinite(weightSum) ? weightSum : '—'} %{Math.abs(weightSum - 100) < 0.001 ? '.' : '; tienen que sumar 100 %.'}
          </div>
        </div>
      </Card>

      <Card title="Avisos por correo">
        <div className="stack">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Aviso</th>
                  <th>Destinatario</th>
                  <th className="right">Activo</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFICATIONS.map((n) => (
                  <tr key={n.key}>
                    <td>{n.label}</td>
                    <td className="muted">{n.who}</td>
                    <td className="right">
                      <input
                        type="checkbox"
                        aria-label={n.label}
                        checked={draft.notifications[n.key]}
                        onChange={(e) => set({ notifications: { ...draft.notifications, [n.key]: e.target.checked } })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid cols-2">
            <Field label="Recordatorios (días tras el envío)" hint="Separados por comas. Por defecto: 3, 5.">
              <input className="input" value={draft.reminderDays} onChange={(e) => set({ reminderDays: e.target.value })} />
            </Field>
            <Field label="Correos de compras" hint="Uno por línea. Si se deja vacío, se avisa a los usuarios con perfil de compras.">
              <textarea className="textarea" value={draft.buyerEmails} onChange={(e) => set({ buyerEmails: e.target.value })} />
            </Field>
          </div>
          <div className="row" style={{ gap: 16 }}>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.weeklyDigest.enabled}
                onChange={(e) => set({ weeklyDigest: { ...draft.weeklyDigest, enabled: e.target.checked } })}
              />
              Enviar a cada proveedor su cartera
            </label>
            <select
              className="select"
              style={{ width: 'auto' }}
              value={draft.weeklyDigest.weekday}
              disabled={!draft.weeklyDigest.enabled}
              onChange={(e) => set({ weeklyDigest: { ...draft.weeklyDigest, weekday: Number(e.target.value) } })}
              aria-label="Día de la semana"
            >
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  cada {d.toLowerCase()}
                </option>
              ))}
            </select>
            <span className="muted">a las</span>
            <input
              className="input"
              style={{ width: 70 }}
              inputMode="numeric"
              value={draft.weeklyDigest.hour}
              disabled={!draft.weeklyDigest.enabled}
              onChange={(e) => set({ weeklyDigest: { ...draft.weeklyDigest, hour: e.target.value } })}
              aria-label="Hora"
            />
            <span className="muted">h (hora de Madrid)</span>
          </div>
        </div>
      </Card>

      <Card title="Servidor de correo (SMTP)">
        <div className="stack">
          <div className="grid cols-3">
            <Field label="Servidor">
              <input className="input" placeholder="smtp.empresa.es" value={draft.smtp.host} onChange={(e) => set({ smtp: { ...draft.smtp, host: e.target.value } })} />
            </Field>
            <Field label="Puerto">
              <input className="input" inputMode="numeric" value={draft.smtp.port} onChange={(e) => set({ smtp: { ...draft.smtp, port: e.target.value } })} />
            </Field>
            <Field label="Cifrado">
              <select className="select" value={draft.smtp.secure ? 'tls' : 'starttls'} onChange={(e) => set({ smtp: { ...draft.smtp, secure: e.target.value === 'tls' } })}>
                <option value="starttls">STARTTLS (normalmente puerto 587)</option>
                <option value="tls">TLS directo (normalmente puerto 465)</option>
              </select>
            </Field>
            <Field label="Usuario">
              <input className="input" autoComplete="off" value={draft.smtp.username} onChange={(e) => set({ smtp: { ...draft.smtp, username: e.target.value } })} />
            </Field>
            <Field label="Contraseña" hint={draft.smtp.hasPassword ? 'Hay una guardada. Déjala vacía para conservarla.' : 'Sin contraseña guardada.'}>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={draft.smtp.password}
                onChange={(e) => set({ smtp: { ...draft.smtp, password: e.target.value } })}
              />
            </Field>
            <Field label="Remitente" hint="Ej.: Compras IMAR <compras@imar.es>">
              <input className="input" value={draft.smtp.from} onChange={(e) => set({ smtp: { ...draft.smtp, from: e.target.value } })} />
            </Field>
          </div>
          <div className="row">
            <input className="input" style={{ maxWidth: 280 }} placeholder="correo para la prueba" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <button className="btn" disabled={testing || testTo.trim() === ''} onClick={() => void onTest()}>
              {testing ? 'Enviando…' : 'Enviar correo de prueba'}
            </button>
            <span className="subtle">La prueba usa la configuración guardada: guarda antes de probar.</span>
          </div>
        </div>
      </Card>

      <Card title="Planos">
        <div className="grid cols-2">
          <Field label="Carpeta del repositorio" hint="Carpeta, dentro del servidor de Provalia, donde se replican los planos. Nunca el servidor de diseño.">
            <input className="input" placeholder="/planos/imar" value={draft.drawings.basePath} onChange={(e) => set({ drawings: { ...draft.drawings, basePath: e.target.value } })} />
          </Field>
          <Field label="Patrón de los ficheros" hint="Sin extensión. Variables {item} y {version}. Ej.: {item}/{version}/{item}">
            <input className="input mono" value={draft.drawings.pattern} onChange={(e) => set({ drawings: { ...draft.drawings, pattern: e.target.value } })} />
          </Field>
        </div>
      </Card>

      <ErrorBox error={saveError} />
      <div className="row end">
        <button className="btn" onClick={() => void reload()} disabled={saving}>
          Descartar cambios
        </button>
        <button className="btn primary" onClick={() => void onSave()} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
